/**
 * Totem pole — a stack of logo marks that falls in from above the screen under
 * real physics and settles into a pole. The pieces stay live afterwards: they
 * can be dragged, thrown across the page, and knocked over.
 *
 * MATTER FROM THE START
 *
 * There is no CSS/GSAP version of the pole. The DOM markup exists only as the
 * source of the artwork and the sizing: the script reads each segment's SVG and
 * measured width, then hides the stack and hands everything to Matter.js. That
 * keeps the artwork in the template (where it can be edited) without the
 * animation having to fake physics.
 *
 * THE FALL
 *
 * Pieces start ABOVE the viewport and drop in, released lowest-first so they
 * land in stack order — the 404 is released third, so it settles into the
 * middle of the pole. They are not locked upright afterwards: they rest
 * wherever they land, so the pole ends up very slightly imperfect, which is
 * what makes it read as stacked rather than drawn.
 *
 * Getting a stack to actually STAND in a 2D engine takes some care:
 *
 *  1. High friction, low restitution. Slippery or bouncy blocks slide off each
 *     other and the pole never forms.
 *
 *  2. Generous `positionIterations` and a tight `slop`. Matter's defaults let
 *     tall stacks jitter themselves apart and visibly sink into each other.
 *
 *  3. Released with a gap between them. Two pieces spawned touching are already
 *     overlapping as far as the solver is concerned, and it resolves that by
 *     firing them apart.
 *
 * matter-js is imported dynamically so it stays a separate chunk, but unlike
 * the earlier click-triggered version it IS fetched on load — the fall is the
 * page's main visual, so there is nothing to defer.
 *
 * No-ops when the markup isn't present, and leaves the plain DOM stack standing
 * for anyone who has asked for reduced motion.
 */
function initTotemPole() {
  document.querySelectorAll("[data-totem-pole]").forEach((pole) => {
    if (pole.hasAttribute("data-totem-initialized")) return;
    pole.setAttribute("data-totem-initialized", "");

    const segments = Array.from(pole.querySelectorAll("[data-totem-segment]"));
    if (!segments.length) return;

    // No physics at all — the CSS stack is already a correctly assembled pole,
    // so showing it as-is is the right reduced-motion result.
    //
    // It has to be un-hidden explicitly: `.js .error-404__stack` hides it from
    // first paint so the white DOM pole never flashes before the canvas takes
    // over, and on this path the canvas never arrives.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const stack = pole.querySelector(".error-404__stack");
      if (stack) stack.style.visibility = "visible";
      return;
    }

    startPhysics(pole, segments);
  });
}

async function startPhysics(pole, segments) {
  // Measured BEFORE the stack is hidden — this is where the artwork and its
  // real rendered size come from.
  const poleRect = pole.getBoundingClientRect();
  const pieces = segments.map((segment) => {
    // Each segment wraps its own mark AND everything above it, so the mark
    // inside is the actual block rather than the segment's own box.
    const mark = segment.querySelector(".totem-mark");
    const node = mark || segment;
    const rect = node.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      svg: node.outerHTML,
    };
  });

  const { Engine, Render, Runner, Bodies, Composite, Mouse, MouseConstraint, Body, Events } =
    await import("matter-js");

  // The physics world is the whole 404 SECTION, not the pole's own column, so
  // pieces can be thrown right across the page — but bounded by the section so
  // they never reach the header or footer.
  //
  // Everything below is in SECTION coordinates. The canvas is appended to the
  // section and sized to it, so mixing in viewport values (as an earlier
  // version did) put the pole off-centre by the section's own offset.
  const section = pole.closest(".error-404") || pole;
  const sectionRect = section.getBoundingClientRect();

  const width = sectionRect.width;
  const height = sectionRect.height;

  // Where the pole's column sits within the section.
  const poleCentreX = poleRect.left - sectionRect.left + poleRect.width / 2;

  const engine = Engine.create();
  // Slightly under Matter's 1.0 default. The pieces fall a long way — from well
  // above the viewport — so at 1.4 they arrived fast enough to knock the stack
  // apart on landing. Lower gravity means a softer impact and a stack that
  // holds together, at the cost of a marginally slower fall.
  engine.world.gravity.y = 0.9;
  // Defaults let a tall stack jitter itself apart; these keep the contacts
  // stable enough for the pole to stand.
  engine.positionIterations = 16;
  engine.velocityIterations = 12;

  const canvas = document.createElement("canvas");
  canvas.className = "error-404__canvas";
  // Appended to the SECTION, not the pole — the pole's column is only a few
  // hundred pixels wide, far too narrow to throw anything across.
  section.appendChild(canvas);

  // Used for BOTH the renderer and the sprite textures — the two must agree or
  // every piece renders at the wrong size.
  const pixelRatio = window.devicePixelRatio || 1;

  const render = Render.create({
    canvas,
    engine,
    options: {
      background: "transparent",
      wireframes: false,
      width,
      height,
      pixelRatio,
    },
  });

  // --- Walls ------------------------------------------------------------
  //
  // The floor is the bottom of the section, which is where the pole stands.
  const floorY = height;

  const WALL = 200;
  Composite.add(engine.world, [
    Bodies.rectangle(width / 2, floorY + WALL / 2, width + WALL * 2, WALL, {
      isStatic: true,
      friction: 1,
      render: { visible: false },
    }),
    // Side walls at the viewport edges, so a flung piece stays on screen.
    Bodies.rectangle(-WALL / 2, height / 2, WALL, height * 4, {
      isStatic: true,
      render: { visible: false },
    }),
    Bodies.rectangle(width + WALL / 2, height / 2, WALL, height * 4, {
      isStatic: true,
      render: { visible: false },
    }),
  ]);

  // --- The falling pieces ------------------------------------------------
  const bodies = pieces.map((piece, index) =>
    Bodies.rectangle(
      // Dropped down the POLE's column, not the middle of the viewport, so
      // they always land as a stack where the column is.
      //
      // A modest horizontal scatter: enough that the pole is never identical
      // twice and often ends up slightly askew, but small enough that the
      // pieces still land on each other and a tower reliably forms.
      //
      // This was ±30% of a piece width, which missed often enough that the
      // stack regularly collapsed on load.
      poleCentreX + randomBetween(-piece.width * 0.14, piece.width * 0.14),
      // Stacked ABOVE the viewport, in the order they should land: index 0
      // lowest (so it arrives first and becomes the base), the top piece
      // highest. The 1.45 multiplier leaves a clear gap between them — two
      // bodies spawned touching count as overlapping to the solver, which
      // resolves it by firing them apart. Tighter than the 1.9 it was: a
      // shorter drop means less speed at impact and a steadier landing.
      //
      // The random extra height varies the gaps, so the pieces arrive at
      // slightly different intervals each load rather than in lockstep.
      -80 - piece.height * (index + 1) * 1.45 - randomBetween(0, piece.height * 0.5),
      piece.width,
      piece.height,
      {
        // Grippy and dead: a slippery or bouncy block slides off the stack and
        // the pole never forms.
        friction: 0.9,
        frictionStatic: 1.2,
        restitution: 0.02,
        // Heavier, so a given throw moves them less.
        density: 0.02,
        // Matter's default. Higher values damp throws, but they apply
        // constantly — a value big enough to slow a flick also made the pieces
        // drift down as though through treacle, and made dragging feel heavy.
        // The velocity cap below handles runaway throws instead.
        frictionAir: 0.01,
        // Tighter than the 0.05 default. Slop is how much overlap the solver
        // tolerates, and at the default a four-high stack visibly sinks into
        // itself before settling.
        slop: 0.02,
        // A tilt and slow spin on the way down, so the pieces look dropped
        // rather than placed. Deliberately modest — at the previous ±0.45rad
        // (~26deg) they frequently landed on a corner and the stack toppled.
        // The high friction below then holds whatever lean they settle at.
        angle: randomBetween(-0.18, 0.18),
        angularVelocity: randomBetween(-0.025, 0.025),
        render: {
          sprite: {
            // Rasterised at DEVICE resolution so it stays sharp, then scaled
            // back by the same factor — otherwise Matter draws the texture at
            // its full pixel size and every piece appears twice as large.
            texture: svgToDataUri(
              piece.svg,
              piece.width * pixelRatio,
              piece.height * pixelRatio,
            ),
            xScale: 1 / pixelRatio,
            yScale: 1 / pixelRatio,
          },
        },
      },
    ),
  );

  // ALL added at once, before the runner starts. The stack order comes from
  // their spawn HEIGHTS — each piece starts further up than the one below it,
  // so gravity alone delivers them lowest-first.
  //
  // They were previously staggered with setTimeout, which meant the world sat
  // empty for the first fraction of a second and the fall appeared to start
  // late rather than being underway on load.
  Composite.add(engine.world, bodies);

  // --- Interaction --------------------------------------------------------
  const mouse = Mouse.create(render.canvas);
  const mouseConstraint = MouseConstraint.create(engine, {
    mouse,
    constraint: {
      // Responsive: the piece tracks the cursor closely, so dragging feels
      // direct rather than sluggish. Runaway throws are held back by the
      // velocity cap further down instead of by softening the drag, which is
      // what made it feel heavy.
      stiffness: 0.2,
      render: { visible: false },
    },
  });
  Composite.add(engine.world, mouseConstraint);

  // Matter swallows wheel and touch events by default, which would trap the
  // page's scroll whenever the pointer is over the canvas.
  mouse.element.removeEventListener("mousewheel", mouse.mousewheel);
  mouse.element.removeEventListener("DOMMouseScroll", mouse.mousewheel);
  mouse.element.removeEventListener("touchstart", mouse.mousedown);
  mouse.element.removeEventListener("touchmove", mouse.mousemove);
  mouse.element.removeEventListener("touchend", mouse.mouseup);
  mouse.element.addEventListener("touchstart", mouse.mousedown, { passive: true });
  mouse.element.addEventListener("touchmove", (event) => {
    if (mouseConstraint.body) mouse.mousemove(event);
  });
  mouse.element.addEventListener("touchend", (event) => {
    if (mouseConstraint.body) mouse.mouseup(event);
  });

  // A hard ceiling on how fast a piece can travel, whatever the drag did.
  // The soft constraint and air friction above shape normal throws; this is
  // the backstop that stops a violent flick sending one off at a speed the
  // collision solver cannot resolve — at which point pieces tunnel straight
  // through the walls and vanish.
  const MAX_SPEED = 22;
  Events.on(engine, "afterUpdate", () => {
    bodies.forEach((body) => {
      const { x, y } = body.velocity;
      const speed = Math.hypot(x, y);
      if (speed <= MAX_SPEED) return;

      const scale = MAX_SPEED / speed;
      Body.setVelocity(body, { x: x * scale, y: y * scale });
    });
  });

  Render.run(render);
  Runner.run(Runner.create(), engine);
}

// The falling pieces are drawn in a DARK tone rather than the white the DOM
// pole uses. They are thrown around over the copy, and white blocks made the
// heading and links behind them unreadable — this is --darkest lifted ~10%
// toward white, the same token the case-study cards use.
const PIECE_FILL = "#33302D";
// A hairline edge so the dark pieces still read against the dark page.
const PIECE_STROKE = "rgba(255,255,255,0.35)";

/** A random float in [min, max). */
function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

/**
 * Turns a segment's inline SVG into a data URI Matter can use as a sprite.
 *
 * `currentColor` has to be substituted out: a data URI has no CSS context to
 * inherit from, so anything left as currentColor renders black.
 */
function svgToDataUri(markup, width, height, fill = PIECE_FILL) {
  const sized = markup
    .replace(/currentColor/g, fill)
    // A hairline outline. The fill is only slightly lighter than the page
    // background, so without this the pieces would be near-invisible against
    // it — the outline is what keeps their silhouette readable.
    .replace(/<path /g, `<path stroke="${PIECE_STROKE}" stroke-width="0.6" `)
    .replace(/<svg /, `<svg width="${Math.round(width)}" height="${Math.round(height)}" `);

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized)}`;
}

export { initTotemPole };
